import { HelpCategoryMeta, HelpTopic } from './help.types';

export const HELP_CATEGORIES: HelpCategoryMeta[] = [
  {
    id: 'ALL',
    label: 'Todos os Tópicos',
    iconName: 'BookOpen',
    description: 'Visualização completa de toda a documentação e base de conhecimento.'
  },
  {
    id: 'OVERVIEW',
    label: 'Visão Geral & Início',
    iconName: 'Compass',
    description: 'Apresentação do sistema, fluxo geral da fábrica e conceitos fundamentais.'
  },
  {
    id: 'ORDERS_PCP',
    label: 'Pedidos & Produção (PCP)',
    iconName: 'ClipboardList',
    description: 'Fila de pedidos, cronômetros setoriais, lançamentos manuais e apontamento de tempos.'
  },
  {
    id: 'ERP_INTEGRATION',
    label: 'Integração ERP ESW',
    iconName: 'RefreshCw',
    description: 'Sincronização com a API ESW, esteira de validação, mapeamento de produtos e liberação.'
  },
  {
    id: 'ENGINEERING_PRODUCTS',
    label: 'Engenharia de Produtos',
    iconName: 'Boxes',
    description: 'Cadastro de coifas, partes estruturais, acessórios recomendados e projetos técnicos.'
  },
  {
    id: 'CATEGORIES',
    label: 'Categorias do Catálogo',
    iconName: 'FolderTree',
    description: 'Organização de categorias, vínculos e distinção entre produtos finais, partes e acessórios.'
  },
  {
    id: 'SETTINGS_RBAC',
    label: 'Usuários & Permissões (RBAC)',
    iconName: 'ShieldCheck',
    description: 'Gerenciamento de usuários, perfis (ADM, PCP, Operadores), permissões granulares e segurança.'
  },
  {
    id: 'PROJECTOR_TV',
    label: 'Modo TV / Projetor Fabril',
    iconName: 'Tv',
    description: 'Exibição em tela cheia na fábrica, rotação de pedidos e comunicados da diretoria.'
  },
  {
    id: 'REPORTS_AUDIT',
    label: 'Relatórios & Auditoria',
    iconName: 'FileText',
    description: 'Exportações em PDF, relatórios com layouts personalizados e logs de auditoria detalhados.'
  },
  {
    id: 'RULES_GLOSSARY',
    label: 'Regras & Glossário',
    iconName: 'Cpu',
    description: 'Regras de negócio, cálculos automáticos de tempo e travas de segurança do sistema.'
  }
];

export const HELP_TOPICS: HelpTopic[] = [
  // =========================================================================
  // 1. VISÃO GERAL
  // =========================================================================
  {
    id: 'overview-system',
    categoryId: 'OVERVIEW',
    categoryName: 'Visão Geral & Início',
    title: 'Visão Geral do Sistema e Arquitetura Fabril',
    subtitle: 'Entenda o objetivo do sistema e como ele conecta vendas, engenharia e chão de fábrica',
    badge: 'Conceito Central',
    iconName: 'Compass',
    overview: 'O sistema é uma plataforma completa de Gestão de Chão de Fábrica e Controle da Produção (PCP) especializada na fabricação sob medida de coifas industriais e residenciais, dutos e estruturas em aço inoxidável.',
    whatItDoes: [
      'Centraliza pedidos importados diretamente do ERP ESW e pedidos criados localmente.',
      'Controla o ciclo de vida de cada peça por meio de uma esteira sequencial de setores (Corte, Dobra, Solda, Acabamento, Acessórios, Embalagem, Logística/Instalação).',
      'Registra com precisão milimétrica os tempos reais de fabricação por operador e setor.',
      'Disponibiliza telas públicas em tempo real para TV/Projetor no chão de fábrica sem necessidade de login.',
      'Oferece dashboards executivos e relatórios de auditoria para tomada de decisões pela diretoria.'
    ],
    howToDoSteps: [
      {
        number: 1,
        title: 'Acessar a Plataforma',
        description: 'Faça login com seu e-mail e senha. As permissões e o setor ativo serão carregados automaticamente.'
      },
      {
        number: 2,
        title: 'Navegar pelo Menu Lateral',
        description: 'Utilize o menu lateral para alternar entre Dashboard Geral, Fila de Pedidos, Produtos, Categorias, Relatórios, Configurações e Ajuda.'
      },
      {
        number: 3,
        title: 'Operar no Chão de Fábrica',
        description: 'Na tela de Pedidos, localize a ordem de produção, abra o item e registre o início ou término dos períodos produtivos.'
      }
    ],
    rulesAndValidations: [
      {
        title: 'Segurança de Sessão por Inatividade',
        description: 'Por motivo de segurança operacional, após 120 minutos de inatividade, o sistema desconecta o usuário automaticamente.',
        type: 'SECURITY'
      },
      {
        title: 'Persistência Real no Banco de Dados',
        description: 'Todas as operações são gravadas de forma permanente no banco de dados relacional MySQL com rastreabilidade completa.',
        type: 'AUTOMATION'
      }
    ],
    tipsAndBestPractices: [
      'Mantenha sempre seu perfil com o setor de atuação correto para agilizar o preenchimento automático nos apontamentos.',
      'Utilize a tela de Projetor (/projetor) em monitores dedicados instalados no galpão para orientar os operadores visivelmente.'
    ],
    keywords: ['visão geral', 'introdução', 'arquitetura', 'login', 'sessão', 'conceito', 'início', 'fábrica', 'pcp']
  },

  // =========================================================================
  // 2. PEDIDOS E PCP (CHÃO DE FÁBRICA)
  // =========================================================================
  {
    id: 'orders-management',
    categoryId: 'ORDERS_PCP',
    categoryName: 'Pedidos & Produção (PCP)',
    title: 'Gestão da Fila de Pedidos & Prioridades',
    subtitle: 'Como gerenciar a fila produtiva, classificar prioridades e acompanhar status',
    badge: 'Chão de Fábrica',
    iconName: 'ClipboardList',
    overview: 'A tela de Pedidos (/orders) é o coração operacional do sistema. Nela, o PCP e os operadores visualizam todas as ordens de serviço ativas, seus prazos de entrega, cidades de destino, status atuais e itens anexos.',
    whatItDoes: [
      'Exibe pedidos em formato de tabela com identificação visual de cores por setor.',
      'Sinaliza pedidos com etiquetas especiais de "Emergência" (borda vermelha destacada) e "Prioridade" (borda amarela).',
      'Permite buscar instantaneamente por número do pedido, nome do cliente, cidade ou produto.',
      'Filtra pedidos por setor de destino, status de produção, datas de saída e prioridades.',
      'Permite reordenar a fila de produção arrastando e soltando (Drag & Drop).'
    ],
    howToDoSteps: [
      {
        number: 1,
        title: 'Localizar o Pedido',
        description: 'Utilize o campo de busca no topo da tela ou selecione os filtros rápidos de setor ou status.'
      },
      {
        number: 2,
        title: 'Expandir os Itens do Pedido',
        description: 'Clique na linha do pedido ou no botão "Ver Itens" para visualizar todas as peças e acessórios que compõem o lote.'
      },
      {
        number: 3,
        title: 'Abrir o Painel de Apontamento',
        description: 'Clique sobre o item desejado para abrir a janela de lançamento de períodos de produção e cronômetro.'
      }
    ],
    rulesAndValidations: [
      {
        title: 'Identificação Visual por Cores de Setor',
        description: 'Cada setor possui uma cor oficial padronizada (ex: Corte #dc3545, Dobra #fd7e14, Solda #0d6efd, Acabamento #6f42c1, Embalagem #198754).',
        type: 'AUTOMATION'
      },
      {
        title: 'Status Automático do Pedido Pai',
        description: 'O status do pedido pai é derivado automaticamente do estado dos seus itens: se todos estiverem finalizados, o pedido é marcado como "Finalizado"; se ao menos um item estiver em produção, o pedido assume "Produção".',
        type: 'CALCULATION'
      }
    ],
    tipsAndBestPractices: [
      'Pedidos com tag de Emergência devem sempre ter prioridade de fabricação em todos os setores da fábrica.',
      'Mantenha as datas de saída sempre atualizadas para que os cálculos de prazos e atrasos no Dashboard fiquem corretos.'
    ],
    keywords: ['pedidos', 'fila', 'pcp', 'emergência', 'prioridade', 'busca', 'filtros', 'drag drop', 'status', 'itens']
  },
  {
    id: 'orders-period-recording',
    categoryId: 'ORDERS_PCP',
    categoryName: 'Pedidos & Produção (PCP)',
    title: 'Apontamento de Períodos de Produção & Cronômetro',
    subtitle: 'Como registrar horários de início/fim, pausas, colaboradores e ocorrências em cada setor',
    badge: 'Operação',
    iconName: 'Timer',
    overview: 'Cada peça de um pedido possui seu próprio histórico de períodos de produção. O modal de apontamento permite ao operador ou líder disparar o cronômetro em tempo real ou lançar retroativamente os horários de início, término e pausas.',
    whatItDoes: [
      'Permite acionar o botão "Iniciar Agora" para gravar a data, hora exata e o nome do colaborador logado.',
      'Permite pausar e registrar o término de um período com o botão "Pausar / Finalizar".',
      'Possibilita o lançamento manual de múltiplos turnos/períodos para o mesmo setor ou setores distintos.',
      'Calcula e exibe a soma total de tempo gasto na peça (em horas e minutos).',
      'Permite registrar ocorrências operacionais (ex.: falta de matéria-prima, retrabalho, ajuste de máquina).'
    ],
    howToDoSteps: [
      {
        number: 1,
        title: 'Abrir a Peça',
        description: 'Na tela de pedidos, clique sobre a linha da peça que deseja apontar.'
      },
      {
        number: 2,
        title: 'Iniciar ou Adicionar Período',
        description: 'Para apontamento em tempo real, clique em "Iniciar Agora". Para lançamento manual, utilize o botão "Adicionar Período" e informe Data, Hora Inicial e Hora Final.'
      },
      {
        number: 3,
        title: 'Selecionar Colaborador e Setor',
        description: 'Confirme o setor ativo (ex: Dobra) e o nome do operador responsável pelo trabalho.'
      },
      {
        number: 4,
        title: 'Gravar no Banco de Dados',
        description: 'Clique em "Gravar Períodos". Os dados serão salvos permanentemente no banco e o status do pedido será atualizado instantaneamente.'
      }
    ],
    rulesAndValidations: [
      {
        title: 'Formato de Horário Válido',
        description: 'Os horários devem seguir o formato militar HH:MM (ex: 08:30, 14:45). O horário de término não pode ser anterior ao horário de início no mesmo dia.',
        type: 'RESTRICTION'
      },
      {
        title: 'Acúmulo Transparente de Períodos',
        description: 'Múltiplos períodos no mesmo setor são somados para compor o tempo total consumido da ordem de serviço.',
        type: 'CALCULATION'
      },
      {
        title: 'Transição Automática de Status',
        description: 'Ao registrar um período em aberto (com início mas sem término), o status da peça muda para "Produção/[Setor]". Ao preencher todos os términos e concluir, o item pode ser avançado.',
        type: 'AUTOMATION'
      }
    ],
    tipsAndBestPractices: [
      'Sempre preencha o campo de ocorrências caso haja qualquer parada inesperada ou retrabalho para fins de auditoria de qualidade.',
      'Se o colaborador trocar de turno antes de terminar a peça, encerre o período atual e abra um novo com o nome do próximo operador.'
    ],
    keywords: ['apontamento', 'períodos', 'cronômetro', 'tempo', 'horas', 'início', 'pausa', 'operador', 'setor', 'ocorrências']
  },

  // =========================================================================
  // 3. INTEGRAÇÃO ERP ESW & VALIDAÇÃO DE PEDIDOS
  // =========================================================================
  // 3. INTEGRAÇÃO ERP ESW
  // =========================================================================
  {
    id: 'erp-sync-validation',
    categoryId: 'ERP_INTEGRATION',
    categoryName: 'Integração ERP ESW',
    title: 'Sincronização com ERP ESW & Fila de Validação',
    subtitle: 'Entenda como os pedidos chegam da API ESW, limites de período e esteira de aprovação',
    badge: 'Integração Oficial',
    iconName: 'RefreshCw',
    overview: 'O sistema é integrado via API REST oficial com o ERP ESW (https://www_api.esw.net.br/public_html/api/bi/Pedidos). Para proteger o chão de fábrica contra inconsistências cadastrais e sobrecargas na API, os pedidos passam por uma esteira de validação (erp_pending_orders) com controle estrito de períodos de consulta.',
    whatItDoes: [
      'Consulta os pedidos de venda do ERP com filtro customizado de datas (limite máximo de até 1 ano / 365 dias).',
      'Inicia os campos de data DE e ATÉ vazios por padrão para evitar consultas acidentais de períodos antigos ou excessivos.',
      'Mantém o botão "Filtrar ERP" inativo até o preenchimento de um período válido de até 365 dias.',
      'Exibe avisos preventivos imediatos em tempo real (pré-clique) caso o intervalo ultrapasse 1 ano ou contenha datas invertidas.',
      'Disponibiliza botões de atalho rápido (30 dias, 90 dias, 180 dias, 1 ano máx e Limpar) para seleção instantânea de intervalos seguros.',
      'Armazena os pedidos recebidos na tabela erp_pending_orders com status "PENDENTE".',
      'Identifica automaticamente quais produtos do pedido já estão cadastrados e quais precisam de mapeamento.',
      'Exibe a contagem de produtos pendentes por pedido antes da liberação.',
      'Permite aprovar e liberar o pedido para a produção ativa ou rejeitar o pedido descartando-o da fila.'
    ],
    howToDoSteps: [
      {
        number: 1,
        title: 'Abrir o Painel ERP',
        description: 'Na tela de Pedidos, clique no botão "Painel de Validação e Liberação" (ícone de sincronização no topo).'
      },
      {
        number: 2,
        title: 'Definir Período Válido (Máx. 1 Ano)',
        description: 'Os campos de data DE e ATÉ iniciam vazios. Digite o período desejado no formato DD/MM/AAAA ou clique em um dos atalhos rápidos (30 dias, 90 dias, 180 dias ou 1 ano máx).'
      },
      {
        number: 3,
        title: 'Verificar Validação Visual Pré-Clique',
        description: 'Observe a indicação visual abaixo das datas: se o intervalo for válido, um indicador verde confirmará os dias selecionados e o botão "Filtrar ERP" ficará azul e habilitado. Se o intervalo exceder 365 dias, um alerta amarelo aparecerá e o botão permanecerá inativo.'
      },
      {
        number: 4,
        title: 'Filtrar e Analisar Itens Pendentes',
        description: 'Clique em "Filtrar ERP". Verifique se há itens marcados em vermelho com "Produto Não Mapeado". Se houver, utilize o botão de mapeamento rápido de 1-clique.'
      },
      {
        number: 5,
        title: 'Liberar para a Produção',
        description: 'Quando todos os itens estiverem verdes/mapeados, informe a cidade de entrega se necessário e clique em "Aprovar & Liberar". O pedido entrará imediatamente na fila do chão de fábrica.'
      }
    ],
    rulesAndValidations: [
      {
        title: 'Regra Estrita de Período Máximo de 1 Ano (365 Dias)',
        description: 'A API do ERP ESW não aceita intervalos de consulta superiores a 365 dias para prevenir timeout e sobrecarga. O sistema impede requisições maiores mantendo o botão "Filtrar ERP" desabilitado e emitindo alertas preventivos.',
        type: 'RESTRICTION'
      },
      {
        title: 'Inicialização Limpa dos Inputs de Data',
        description: 'Os campos DE e ATÉ iniciam limpos (sem valores pré-fixados de anos anteriores), exigindo a escolha intencional do operador ou o uso dos atalhos rápidos.',
        type: 'AUTOMATION'
      },
      {
        title: 'Avisos Preventivos Pré-Clique',
        description: 'Validação instantânea no frontend que calcula a diferença de dias conforme a digitação, exibindo alertas detalhados sobre período excedido (> 365 dias), datas invertidas ou incompletas antes que o usuário clique em filtrar.',
        type: 'SECURITY'
      },
      {
        title: 'Validação Backend Redundante contra Rate Limit',
        description: 'As rotas do backend (server.ts e api/index.php) validam o intervalo recebido e barram consultas com diff > 365 dias com código HTTP 400, protegendo o IP da fábrica contra bloqueios.',
        type: 'SECURITY'
      },
      {
        title: 'Trava de Segurança contra Produtos Não Cadastrados',
        description: 'O sistema bloqueia a aprovação de qualquer pedido que contenha itens com código ERP não vinculado a um produto do catálogo interno.',
        type: 'RESTRICTION'
      },
      {
        title: 'Proteção de Rate Limit do ERP ESW',
        description: 'A API remota da ESW exige intervalo de ~180 segundos entre varreduras completas. Quando a proteção estiver ativa, o sistema exibe o aviso com tempo restante e opera com os pedidos já armazenados no banco sem travar a interface.',
        type: 'SECURITY'
      },
      {
        title: 'Geração do Número de Pedido de Fábrica',
        description: 'Ao aprovar, o sistema converte o id_pedido_venda do ERP no número oficial da ordem de serviço com prioridade e setor inicial.',
        type: 'AUTOMATION'
      }
    ],
    tipsAndBestPractices: [
      'Utilize os atalhos rápidos "30 dias" ou "90 dias" para capturar os pedidos recentes de forma rápida e segura.',
      'Caso precise consultar um período histórico específico, certifique-se de que a data final não ultrapasse 1 ano a partir da data inicial.',
      'Se um item for um acessório avulso, certifique-se de que ele esteja cadastrado na categoria correta antes de vincular.'
    ],
    keywords: ['erp', 'esw', 'sincronização', 'pedidos pendentes', 'validação', 'liberação', 'aprovar', 'rejeitar', 'rate limit', '1 ano', '365 dias', 'filtro de data', 'atalhos']
  },
  {
    id: 'erp-product-mapping',
    categoryId: 'ERP_INTEGRATION',
    categoryName: 'Integração ERP ESW',
    title: 'Mapeamento Inteligente de Produtos ERP x Catálogo Local & Regras de Acessórios/Partes',
    subtitle: 'Como vincular códigos de produtos do ERP com o cadastro interno e evitar duplicações de acessórios/partes',
    badge: 'Mapeamento & Agrupamento',
    iconName: 'Link',
    overview: 'Muitas vezes, a descrição ou o código do produto no ERP (ex.: código 2045) difere da nomenclatura técnica de engenharia da fábrica (ex.: Coifa Industrial Centro 304). A ferramenta de mapeamento resolve essa equivalência de forma permanente, além de aplicar inteligência de agrupamento para que acessórios (ex: Exaustor de Transmissão) e partes estruturais não fiquem duplicados no pedido.',
    whatItDoes: [
      'Permite selecionar um produto do cadastro interno para associar ao código retornado pelo ERP.',
      'Salva a relação erp_product_id na tabela products.',
      'Identifica automaticamente produtos classificados como Acessórios ou Partes/Peças Estruturais.',
      'Separação Inteligente na Liberação: ao aprovar um pedido com um equipamento principal (ex: Coifa) e seus acessórios/partes vindos do ERP, os acessórios são vinculados diretamente ao checklist do equipamento pai e NÃO são duplicados como itens avulsos na produção.',
      'Propaga o mapeamento instantaneamente para todos os outros pedidos pendentes que contenham o mesmo código.',
      'Atualiza a contagem de produtos faltantes de zero para todos os lotes afetados.'
    ],
    howToDoSteps: [
      {
        number: 1,
        title: 'Identificar o Item Sem Vínculo',
        description: 'No modal de validação do ERP, localize o pedido e a linha do produto destacada com aviso de "Não Mapeado".'
      },
      {
        number: 2,
        title: 'Abrir a Janela de Vínculo',
        description: 'Clique no botão "Vincular a Produto Cadastrado" ou selecione no menu suspenso de catálogo.'
      },
      {
        number: 3,
        title: 'Confirmar a Associação e Visualizar Classificação',
        description: 'Escolha o produto técnico correspondente. O sistema exibirá uma etiqueta indicando se o item é um "Equipamento Principal", "Acessório Anexo" ou "Parte Estrutural" vinculada ao equipamento do pedido.'
      },
      {
        number: 4,
        title: 'Liberar sem Duplicações',
        description: 'Ao clicar em "Liberar para Produção", o sistema cria apenas o equipamento principal na linha fabril com todos os seus acessórios e partes devidamente listados internamente.'
      }
    ],
    rulesAndValidations: [
      {
        title: 'Regra de Não-Duplicação de Acessórios e Partes',
        description: 'Itens mapeados em categorias de "Acessórios" ou "Partes/Peças" (ou contidos nas listas de acessórios/peças de uma Coifa/Equipamento no mesmo pedido) são incorporados ao checklist de montagem e embalagem do equipamento pai, prevenindo que sejam gerados como itens duplicados avulsos na esteira de produção.',
        type: 'AUTOMATION'
      },
      {
        title: 'Vínculo Permanente e Reutilizável',
        description: 'Uma vez mapeado um código do ERP, compras futuras do mesmo código serão reconhecidas automaticamente nas próximas sincronizações.',
        type: 'AUTOMATION'
      },
      {
        title: 'Registro em Log de Auditoria',
        description: 'Toda alteração de erp_product_id é registrada no log de auditoria com o ID do usuário e data/hora.',
        type: 'SECURITY'
      }
    ],
    tipsAndBestPractices: [
      'Se o produto vendido no ERP ainda não existir no catálogo interno, acesse a tela de Produtos, cadastre-o com seus respectivos projetos técnicos e retorne ao modal para vincular.',
      'Cadastre exaustores, dutos e registros na categoria "Acessórios" para que o sistema reconheça automaticamente o vínculo com as coifas do pedido.',
      'Utilize o campo de pesquisa do catálogo para localizar coifas por modelo ou dimensões.'
    ],
    keywords: ['mapeamento', 'erp product id', 'vínculo', 'catálogo', 'equivalência', 'produtos pendentes', 'acessórios', 'exaustor', 'partes', 'duplicação', 'regras']
  },

  // =========================================================================
  // 4. ENGENHARIA DE PRODUTOS & PROJETOS
  // =========================================================================
  {
    id: 'engineering-products-catalog',
    categoryId: 'ENGINEERING_PRODUCTS',
    categoryName: 'Engenharia de Produtos',
    title: 'Cadastro de Produtos, Roteiro Produtivo & Engenharia',
    subtitle: 'Como cadastrar coifas, estruturar peças, definir setores obrigatórios e anexar projetos técnicos',
    badge: 'Engenharia',
    iconName: 'Boxes',
    overview: 'A tela de Produtos (/products/list) gerencia o portfólio completo da Master Coifa. Cada produto possui sua ficha técnica, modelos, imagens fotográficas, pranchas de projeto técnico em alta resolução, lista de peças estruturais, acessórios recomendados e a sequência exata de setores pelos quais o produto deve passar.',
    whatItDoes: [
      'Cadastra produtos com Código ERP, Nome Técnico, Modelo, Categoria e Descrição detalhada.',
      'Permite fazer upload da Imagem Principal do Produto e de 2 Projetos Técnicos de Engenharia (PDF/PNG/JPG).',
      'Oferece visualizador de projetos com recursos de Zoom In, Zoom Out, Reset e Arrastar (Pan).',
      'Configura o Roteiro Produtivo (Sequência de Setores) ativando ou desativando caixas como Corte, Dobra, Solda, etc.',
      'Permite vincular Partes Estruturais e Acessórios Recomendados com 1 clique.'
    ],
    howToDoSteps: [
      {
        number: 1,
        title: 'Novo Produto ou Editar Existente',
        description: 'Clique no botão "Novo Produto" ou selecione um item existente na listagem e clique em "Editar".'
      },
      {
        number: 2,
        title: 'Preencher Dados Básicos & Código ERP',
        description: 'Informe o nome do produto, modelo comercial, categoria (ex: Coifas Industriais) e o código ERP se já existente.'
      },
      {
        number: 3,
        title: 'Definir o Roteiro de Setores',
        description: 'Marque os setores que compõem a fabricação do produto (ex: Corte → Dobra → Solda → Acabamento → Embalagem).'
      },
      {
        number: 4,
        title: 'Anexar Imagens e Pranchas Técnicas',
        description: 'Arraste ou selecione os arquivos da foto do produto e das pranchas de projeto técnico 1 e 2.'
      },
      {
        number: 5,
        title: 'Vincular Acessórios e Peças',
        description: 'No bloco de Acessórios e Partes Estruturais, selecione os itens do catálogo ou digite nomes personalizados.'
      },
      {
        number: 6,
        title: 'Salvar Ficha Técnica',
        description: 'Clique em "Salvar Produto". Todas as informações são persistidas no banco de dados e os arquivos são enviados para o servidor.'
      }
    ],
    rulesAndValidations: [
      {
        title: 'Regras de Filtragem do Catálogo (Acessórios vs Partes)',
        description: 'O seletor de Acessórios lista produtos das categorias "Acessórios". O seletor de Partes lista itens cadastrados como "Partes", "Peças" ou "Partes Estruturais".',
        type: 'RESTRICTION'
      },
      {
        title: 'Roteiro de Produção Padrão',
        description: 'Se nenhum setor for marcado manualmente, o sistema adota a sequência fabril padrão: Corte, Dobra, Solda, Acabamento, Embalagem e Logística.',
        type: 'AUTOMATION'
      },
      {
        title: 'Imagens e Upload Seguro',
        description: 'Os arquivos de imagem e projeto são processados com nomes únicos no servidor e suportam formatos JPG, PNG e WebP.',
        type: 'SECURITY'
      }
    ],
    tipsAndBestPractices: [
      'Sempre anexe o desenho técnico com as cotas exatas de dobra e furação para que o operador possa consultar direto pelo tablet no setor de corte/dobra.',
      'Utilize o zoom na prancha de projeto para inspecionar detalhes milimétricos sem precisar imprimir papel.'
    ],
    keywords: ['produtos', 'engenharia', 'cadastro', 'projetos técnicos', 'zoom', 'partes', 'acessórios', 'roteiro produtivo', 'sequência de setores']
  },

  // =========================================================================
  // 5. CATEGORIAS DO CATÁLOGO
  // =========================================================================
  {
    id: 'categories-management',
    categoryId: 'CATEGORIES',
    categoryName: 'Categorias do Catálogo',
    title: 'Gestão de Categorias e Classificação de Portfólio',
    subtitle: 'Como criar, editar e organizar categorias de coifas, peças e componentes',
    badge: 'Estruturação',
    iconName: 'FolderTree',
    overview: 'A tela de Categorias (/products/categories) permite organizar a árvore mercadológica e operacional da empresa, agrupando produtos por famílias de fabricação.',
    whatItDoes: [
      'Lista todas as categorias cadastradas com contagem de produtos vinculados.',
      'Permite criar novas categorias com nome e descrição de finalidade.',
      'Permite editar nomes e excluir categorias que não estejam sendo utilizadas.',
      'Alimenta automaticamente os filtros de busca da tela de Produtos e Pedidos.'
    ],
    howToDoSteps: [
      {
        number: 1,
        title: 'Acessar Categorias',
        description: 'No menu lateral, expanda a seção "Produtos" e clique em "Categorias".'
      },
      {
        number: 2,
        title: 'Cadastrar Nova Categoria',
        description: 'Clique em "Nova Categoria", informe o nome (ex.: "Dutos e Conexões", "Acessórios", "Coifas Ilha") e a descrição.'
      },
      {
        number: 3,
        title: 'Salvar',
        description: 'Clique em "Salvar". A categoria passa a ficar disponível imediatamente no cadastro de produtos.'
      }
    ],
    rulesAndValidations: [
      {
        title: 'Integridade Referencial',
        description: 'Categorias que possuam produtos vinculados não podem ser excluídas sem que os produtos sejam antes remanejados.',
        type: 'RESTRICTION'
      },
      {
        title: 'Unicidade de Nomenclatura',
        description: 'Não é permitido cadastrar duas categorias com o mesmo nome exato para evitar duplicidade de filtros.',
        type: 'RESTRICTION'
      }
    ],
    tipsAndBestPractices: [
      'Mantenha categorias bem delineadas para facilitar a filtragem rápida na tela de produção (ex.: Coifas Residenciais vs Coifas Industriais).'
    ],
    keywords: ['categorias', 'catálogo', 'classificação', 'família de produtos', 'portfólio']
  },

  // =========================================================================
  // 6. USUÁRIOS, PERFIS E RBAC
  // =========================================================================
  {
    id: 'rbac-users-permissions',
    categoryId: 'SETTINGS_RBAC',
    categoryName: 'Usuários & Permissões (RBAC)',
    title: 'Controle de Acessos, Perfis e Permissões Granulares (RBAC)',
    subtitle: 'Como gerenciar operadores, supervisores, administradores e matriz de permissões',
    badge: 'Segurança & RBAC',
    iconName: 'ShieldCheck',
    overview: 'O módulo de Configurações (/settings) possui um sistema completo de Controle de Acesso Baseado em Funções (RBAC - Role-Based Access Control). Cada usuário pode ter um perfil pré-configurado ou uma matriz granular de permissões (Ver, Adicionar, Editar, Excluir) por módulo.',
    whatItDoes: [
      'Cadastra novos usuários com Nome, E-mail, Senha Criptografada, Função/Perfil e Setor de Trabalho.',
      'Permite associar operadores a setores específicos (ex.: Dobra, Solda, Corte) para pré-preenchimento nos apontamentos.',
      'Disponibiliza matriz de permissões granulares com 4 ações (View, Add, Edit, Delete) sobre 6 módulos (Pedidos, Produtos, Categorias, Relatórios, Configurações, Usuários).',
      'Permite redefinir senhas, bloquear/ativar usuários ou remover credenciais com segurança.',
      'Controla o perfil especial "ADM" com acesso total a todas as rotas e funções.'
    ],
    howToDoSteps: [
      {
        number: 1,
        title: 'Acessar a Aba Usuários',
        description: 'Acesse o menu "Configurações" e selecione a aba "Usuários & Acessos".'
      },
      {
        number: 2,
        title: 'Criar ou Editar Usuário',
        description: 'Clique em "Novo Usuário" ou no ícone de lápis ao lado do usuário na lista.'
      },
      {
        number: 3,
        title: 'Definir Função e Setor Padrão',
        description: 'Escolha a Função (ex: ADM, PCP, Operador de Setor, Visualizador) e selecione o Setor Oficial (ex: Corte, Solda).'
      },
      {
        number: 4,
        title: 'Customizar Permissões Granulares (Opcional)',
        description: 'Se desejar permissões personalizadas, marque ou desmarque as caixas de seleção na tabela de permissões (Visualizar, Adicionar, Editar, Excluir).'
      },
      {
        number: 5,
        title: 'Salvar Credencial',
        description: 'Clique em "Salvar Usuário". As alterações entram em vigor imediatamente.'
      }
    ],
    rulesAndValidations: [
      {
        title: 'Proteção contra Auto-Exclusão do Administrador',
        description: 'O sistema impede que o usuário administrador atualmente conectado exclua a si próprio ou revogue sua própria permissão de ADM.',
        type: 'SECURITY'
      },
      {
        title: 'Bloqueio de Rotas Não Autorizadas',
        description: 'Se um usuário tentar acessar diretamente pela URL uma página sem permissão (ex.: /settings sem permissão de configuração), o sistema redireciona automaticamente para o Dashboard.',
        type: 'RESTRICTION'
      },
      {
        title: 'Criptografia de Senhas',
        description: 'As senhas dos usuários são armazenadas no banco de dados com hash seguro (Bcrypt / Password Hash nativo), nunca em texto plano.',
        type: 'SECURITY'
      }
    ],
    tipsAndBestPractices: [
      'Crie contas individuais para cada operador de máquina para que os logs de auditoria identifiquem com clareza quem realizou cada apontamento de produção.',
      'Para tablets fixados nas máquinas, configure usuários com perfil restrito de operador.'
    ],
    keywords: ['usuários', 'rbac', 'permissões', 'perfis', 'adm', 'pcp', 'operador', 'senha', 'segurança', 'acessos']
  },

  // =========================================================================
  // 7. MODO PROJETOR / TV DE PRODUÇÃO
  // =========================================================================
  {
    id: 'projector-tv-mode',
    categoryId: 'PROJECTOR_TV',
    categoryName: 'Modo TV / Projetor Fabril',
    title: 'Painel de TV & Projetor de Chão de Fábrica',
    subtitle: 'Como utilizar o painel público em tela cheia para orientação visual dos operadores',
    badge: 'Chão de Fábrica 4.0',
    iconName: 'Tv',
    overview: 'A rota pública /projetor foi desenvolvida para ser exibida em TVs, painéis de LED e projetores suspensos nas linhas de montagem. Ela funciona em modo quiosque/tela cheia, atualiza-se em tempo real sem intervenção humana e não exige login ativo.',
    whatItDoes: [
      'Exibe carrossel dinâmico dos pedidos em andamento na fábrica com número da OS, cliente, setor atual e prazo de saída.',
      'Destaca em vermelho vibrante pedidos de Emergência e em amarelo pedidos de Prioridade.',
      'Apresenta banner inferior com Comunicados e Avisos da Diretoria em tempo real.',
      'Exibe relógio digital com hora, data e contadores de pedidos ativos.',
      'Permite ao PCP ou Gestor gerenciar as mensagens exibidas na TV diretamente pela tela de Configurações.'
    ],
    howToDoSteps: [
      {
        number: 1,
        title: 'Abrir na TV ou Monitor da Fábrica',
        description: 'No navegador da TV ou computador conectado ao projetor, acesse a URL: /#/projetor.'
      },
      {
        number: 2,
        title: 'Ativar Modo Tela Cheia',
        description: 'Pressione a tecla F11 no teclado ou clique no botão de tela cheia para ocultar as barras do navegador.'
      },
      {
        number: 3,
        title: 'Cadastrar Mensagens da Diretoria',
        description: 'Na tela de Configurações (/settings) na aba "Projetor / Mensagens", adicione avisos, metas diárias ou comunicados de segurança (ex.: "Atenção ao uso de EPIs").'
      }
    ],
    rulesAndValidations: [
      {
        title: 'Acesso Livre para Exibição Pública',
        description: 'A rota /projetor é pública para permitir inicialização automática em TVs sem necessidade de digitar credenciais a cada reinicialização.',
        type: 'AUTOMATION'
      },
      {
        title: 'Atualização Automática sem Recarregar Página',
        description: 'O painel consulta periodicamente os novos dados em segundo plano, garantindo que novos pedidos liberados apareçam sem precisar dar F5.',
        type: 'AUTOMATION'
      }
    ],
    tipsAndBestPractices: [
      'Utilize o painel para celebrar metas batidas e alertar a equipe sobre coifas de clientes prioritários que saem no mesmo dia.',
      'Configure navegadores como Chrome ou Edge em modo Kiosk para inicializarem direto na tela do projetor ao ligar a TV.'
    ],
    keywords: ['projetor', 'tv', 'painel de fábrica', 'chão de fábrica', 'tela cheia', 'mensagens', 'comunicados', 'kiosk']
  },

  // =========================================================================
  // 8. RELATÓRIOS & AUDITORIA
  // =========================================================================
  {
    id: 'reports-and-audit',
    categoryId: 'REPORTS_AUDIT',
    categoryName: 'Relatórios & Auditoria',
    title: 'Relatórios Gerenciais, Auditoria & Exportações em PDF',
    subtitle: 'Como gerar relatórios executivos com layouts personalizados, filtros e rastreabilidade',
    badge: 'Gestão Executiva',
    iconName: 'FileText',
    overview: 'A tela de Relatórios (/reports) transforma todos os dados coletados no chão de fábrica em relatórios de inteligência de negócios prontos para impressão ou envio à diretoria em formato PDF.',
    whatItDoes: [
      'Oferece 3 layouts estruturados de relatório: "Classificação Crítica", "Simplificado Resumo" e "Detalhado Auditoria".',
      'Permite customizar o Nome da Empresa no cabeçalho, Rodapé oficial e exibição de assinaturas.',
      'Gera estatísticas de tempos médios por setor (Corte, Dobra, Solda, Acabamento, etc.).',
      'Inclui tabela de Logs de Auditoria com histórico de quem iniciou, pausou ou alterou cada ordem.',
      'Possibilita exportar em PDF formatado com quebras de página profissionais ou imprimir direto.'
    ],
    howToDoSteps: [
      {
        number: 1,
        title: 'Acessar Relatórios',
        description: 'No menu lateral, clique em "Relatórios".'
      },
      {
        number: 2,
        title: 'Selecionar o Tipo de Layout',
        description: 'Escolha entre Classificação Crítica (foco em prazos e atrasos), Resumo Executivo ou Detalhado de Auditoria.'
      },
      {
        number: 3,
        title: 'Configurar Cabeçalho e Filtros',
        description: 'Selecione o período de datas desejado e ajuste os textos de cabeçalho e rodapé se necessário.'
      },
      {
        number: 4,
        title: 'Gerar PDF ou Imprimir',
        description: 'Clique no botão "Imprimir / Salvar em PDF" no canto superior direito para abrir o diálogo de impressão formatado.'
      }
    ],
    rulesAndValidations: [
      {
        title: 'Layout de Impressão Sem Elementos de Navegação',
        description: 'Ao acionar a impressão, os menus, barras de rolagem e botões da interface são automaticamente ocultados pelo CSS de impressão (media print).',
        type: 'AUTOMATION'
      },
      {
        title: 'Rastreabilidade Total de Operações',
        description: 'A tabela de logs registra todas as ações de criação, edição, exclusão e transição de status com carimbo de data/hora (Timestamp).',
        type: 'SECURITY'
      }
    ],
    tipsAndBestPractices: [
      'Utilize o relatório de Classificação Crítica nas reuniões matinais de alinhamento com os líderes de setor.',
      'Salve relatórios semanais em PDF para compor o histórico de desempenho industrial da empresa.'
    ],
    keywords: ['relatórios', 'auditoria', 'pdf', 'impressão', 'layouts', 'tempos médios', 'logs', 'gestão']
  },

  // =========================================================================
  // 9. REGRAS DE NEGÓCIO & GLOSSÁRIO
  // =========================================================================
  {
    id: 'rules-and-calculations',
    categoryId: 'RULES_GLOSSARY',
    categoryName: 'Regras & Glossário',
    title: 'Glossário Técnico, Regras de Negócio & Fórmulas do Sistema',
    subtitle: 'Consulte a definição exata de cada fórmula matemática, regra e sigla utilizada no sistema',
    badge: 'Documentação Técnica',
    iconName: 'Cpu',
    overview: 'Este guia consolida todas as regras de negócio, travas de validação, cálculos automáticos e termos técnicos adotados em toda a plataforma.',
    whatItDoes: [
      'Explica como o sistema calcula prazos de entrega restantes e identifica pedidos em atraso.',
      'Detalha o cálculo da taxa de produtividade e capacidade fabril.',
      'Esclarece as travas de validação que impedem erros operacionais no lançamento de tempos e pedidos.',
      'Fornece um dicionário completo de termos (PCP, OS, Roteiro Produtivo, Peças, Acessórios, Rate Limit).'
    ],
    howToDoSteps: [
      {
        number: 1,
        title: 'Consultar Termo ou Fórmula',
        description: 'Utilize o campo de busca no topo desta página para digitar a sigla ou nome da regra desejada (ex.: "Prazos", "PCP", "Mapeamento").'
      }
    ],
    rulesAndValidations: [
      {
        title: 'Fórmula de Prazo Restante (Days Left)',
        description: 'Dias Restantes = (Data de Saída do Pedido - Data Atual em Dias Corridos). Se o valor for negativo, o pedido é contabilizado automaticamente como Atrasado no Dashboard.',
        type: 'CALCULATION'
      },
      {
        title: 'Fórmula de Taxa de Produtividade (%)',
        description: 'Produtividade = (Pedidos Finalizados / Total de Pedidos no Período) * 100.',
        type: 'CALCULATION'
      },
      {
        title: 'Fórmula de Tempo Total da Peça',
        description: 'Tempo Total = Soma de todos os intervalos (Hora Término - Hora Início) de todos os períodos registrados na peça.',
        type: 'CALCULATION'
      },
      {
        title: 'Trava contra Horários Invertidos',
        description: 'O sistema bloqueia lançamentos onde a Hora Final seja anterior à Hora Inicial na mesma data.',
        type: 'RESTRICTION'
      }
    ],
    tipsAndBestPractices: [
      'Consulte este glossário sempre que houver dúvidas sobre por que um indicador mudou de cor ou por que um pedido foi classificado como prioritário.'
    ],
    keywords: ['glossário', 'regras', 'fórmulas', 'cálculos', 'prazos', 'atrasos', 'produtividade', 'siglas', 'definições']
  }
];

export const HELP_FAQS = [
  {
    question: 'Por que o botão "Filtrar ERP" fica desabilitado/cinza?',
    answer: 'O botão "Filtrar ERP" permanece inativo enquanto as datas DE e ATÉ não forem preenchidas no formato correto (DD/MM/AAAA) ou se o intervalo selecionado for superior a 1 ano (365 dias). A API do ERP ESW não aceita intervalos maiores que 1 ano. Utilize os atalhos rápidos (30d, 90d, 180d ou 1 ano) para preencher rapidamente um período válido.'
  },
  {
    question: 'Por que o botão "Aprovar & Liberar" está desabilitado no Painel do ERP?',
    answer: 'O botão de aprovação é protegido por uma trava de segurança e só fica ativo quando 100% dos produtos do pedido estiverem vinculados ao catálogo interno da fábrica (sem itens em vermelho). Utilize o botão de mapeamento para associar o código do ERP antes de aprovar.'
  },
  {
    question: 'O que significa a mensagem "Limite de consultas atingido (Rate Limit)" ao sincronizar o ERP?',
    answer: 'A API remota do ERP ESW possui uma política de proteção que exige um intervalo de espera (~180 segundos) entre consultas massivas. O sistema exibe o tempo restante no banner superior e permite que você continue trabalhando normalmente com os pedidos pendentes já gravados no banco de dados sem travar a tela.'
  },
  {
    question: 'Como faço para um operador apontar tempo apenas no seu setor?',
    answer: 'No cadastro de Usuários (/settings), selecione o setor oficial do colaborador (ex: "Dobra"). Ao abrir qualquer peça na tela de pedidos, o sistema selecionará automaticamente o setor do operador conectado, bastando clicar em "Iniciar Agora".'
  },
  {
    question: 'Como colocar o Modo TV em exibição contínua na fábrica?',
    answer: 'Abra a URL "/#/projetor" no navegador do monitor da fábrica e pressione a tecla F11 para entrar em modo tela cheia. O painel não requer login e atualiza os pedidos e mensagens em tempo real.'
  },
  {
    question: 'Onde vejo os projetos técnicos e desenhos de corte de uma coifa?',
    answer: 'Na tela de Produtos (/products/list), selecione o produto desejado e clique nas abas "Projeto 1" ou "Projeto 2". Você poderá utilizar os botões de Zoom In, Zoom Out e arrastar a imagem para inspecionar medidas milimétricas.'
  }
];
